import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as Sentry from '@sentry/remix';
import type { Fulfillment, OrderItemData } from '@shared/dream-components';

import type { CalComBooking } from '~/interfaces/cal_com';
import type { CalComFulfillmentMetadata } from '~/interfaces/fulfillment';
import { ProductType } from '~/interfaces/product';
import isUserAgentMobile from '~/utils/isUserAgentMobile';
import toast from '~/utils/toast';

export type ReschedulingData = {
  fulfillmentId: string;
  fulfillmentMetadata: CalComFulfillmentMetadata;
  calComEventTypeSlug: string;
  calComUserName: string;
};

export const useAccessFulfillment = () => {
  const { t } = useTranslation();
  const [isReschedulingModalOpen, setIsReschedulingModalOpen] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [reschedulingData, setReschedulingData] = useState<ReschedulingData | null>(null);

  const accessFulfillment = async (orderItem: OrderItemData, fulfillment: Fulfillment): Promise<null> => {
    return new Promise((resolve, reject) => {
      if (
        orderItem.product_variant.type === ProductType.COACHING_SESSION &&
        orderItem.product_variant.redeemable_metadata?.cal_com_event_slug &&
        orderItem.product_variant.redeemable_metadata?.cal_com_event_username
      ) {
        setIsReschedulingModalOpen(true);
        setReschedulingData({
          fulfillmentId: fulfillment.id,
          fulfillmentMetadata: fulfillment?.metadata,
          calComEventTypeSlug: orderItem.product_variant.redeemable_metadata.cal_com_event_slug,
          calComUserName: orderItem.product_variant.redeemable_metadata.cal_com_event_username,
        });
        return resolve(null);
      }

      const isMobile = isUserAgentMobile(navigator.userAgent);

      // Safari (and some mobile browsers) block window.open() when called
      // outside the synchronous call stack of a user gesture.
      // Open the window now while we're still in the click handler, then
      // set its location once the fetch resolves.
      const newWindow = isMobile ? null : window.open('', '_blank');

      if (newWindow) {
        const label = t('manage.orders.order.cta.accessing');
        newWindow.document.write(
          `<!DOCTYPE html><html><head><title>${label}</title></head>` +
            '<body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;' +
            `font-family:system-ui,-apple-system,sans-serif;">${label}</body></html>`,
        );
        newWindow.document.close();
      }

      const formData = new FormData();
      formData.append('fulfillmentId', fulfillment.id);

      let navigated = false;

      fetch('/access_fulfillment', {
        method: 'POST',
        body: formData,
      })
        .then((response) => response.json())
        .then((result) => {
          if (result.success && result.url) {
            if (result.type === 'file' && result.filename) {
              const link = document.createElement('a');
              link.href = result.url;
              link.download = result.filename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            } else if (isMobile) {
              // Sonner never auto-closes loading toasts.
              const toastId = toast.loading(t('manage.orders.order.cta.redirecting'));
              setTimeout(() => toast.dismiss(toastId), 1500);
              setTimeout(() => {
                window.location.href = result.url;
              }, 500);
            } else if (newWindow) {
              newWindow.location.href = result.url;
              navigated = true;
            } else {
              window.location.href = result.url;
            }
          }
          resolve(null);
        })
        .catch((error) => {
          Sentry.captureException(error);
          reject(error);
        })
        .finally(() => {
          if (newWindow && !navigated) newWindow.close();
        });
    });
  };

  const handleCloseRescheduling = () => {
    setIsRescheduling(false);
    setIsReschedulingModalOpen(false);
    setReschedulingData(null);
  };

  const handleRescheduleBookingSuccess = async (fulfillmentId: string, bookingData: CalComBooking) => {
    setIsRescheduling(true);

    // Call the existing access_fulfillment endpoint with booking_id
    const formData = new FormData();
    formData.append('fulfillmentId', fulfillmentId);
    formData.append('rescheduledBookingUid', bookingData.uid);

    const response = await fetch('/update_fulfillment', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to update fulfillment');
    }

    setIsRescheduling(false);
    setIsReschedulingModalOpen(false);
    setReschedulingData(null);
  };

  return {
    isReschedulingModalOpen,
    isRescheduling,
    handleCloseRescheduling,
    handleRescheduleBookingSuccess,
    reschedulingData,
    accessFulfillment,
  };
};
